import { get, orderBy } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { locale } from '@tcc/i18n';

// constants
import { managementZonesTypes, scriptTypes } from 'constants/scripts';
import placeHolderIcons from 'constants/managementZonesPlaceholders';
import { CROP_CODES_FOR_AVAILABLE_LAYERS, MONTH_ORDER } from './constants';
import { resourceType } from 'constants/loading';
import { SEASONS_ACRONYMS } from 'constants/seasons';

// selectors
import {
  getActiveFieldCountryCode,
  getActiveFieldId,
  getActiveFieldUuid,
  getActiveScriptType
} from 'current/selectors';
import { getActiveCropId } from 'current/currentSelectors';

import { getPlantingPlanParams } from 'planting-plan-form/selectors';
import {
  getSsurgoZonesByFieldId,
  fieldHasSsurgoZones,
  getImportedMapsZonesByFieldId
} from 'imagery/selectors';
import { getFertilityManagementZones } from 'manual/fertility-selectors';
import { getPlantingManagementZones } from 'manual/planting-selectors';
import { getApplicationManagementZones } from 'manual/application-selectors';
import { getPlantingPlanParamsValidationResult } from 'validations/selectors';
import { isResourceTypePending } from 'loading/selectors';

// util
import { getMessage } from 'util/locale';
import { isSsurgoEnabled } from 'current/utils';
import { formatDate } from 'util/formatters/dateFormatter';
import { LATAM_COUNTRIES } from 'constants/countryCodes';

export const getStep = (state) => get(state, 'scriptFlow.activeStep.stepNumber'); // TODO: CFVWEB-3365 Update to use getStepNumber from via script-flow
export const getSelectedMapLayer = (state) => get(state, `manual.generateZones`);
export const getGeneratedZones = (state) => get(getSelectedMapLayer(state), 'zones');
export const getSelectedMapLayerUuid = (state) => get(getSelectedMapLayer(state), 'selectedMapUuid');
export const getCompleteMapYear = (state) => get(state, `manual.completeMapLayers.year`);
export const canGoBack = () => true; // always will be true, just depends on what the destination is...
export const isManagementZonesStep = (state) => {
  return (getStep(state) === 1
    && getActiveScriptType(state) !== scriptTypes.EXPERIMENT);
};
export const getManualScriptParams = (state) => get(state, 'manual.params');
export const getSelectedManagementZonesId = (state) => get(getManualScriptParams(state), 'managementZonesId');
export const getSelectedManagementZonesType = (state) => get(getManualScriptParams(state), 'managementZonesType');
export const getSelectedLayerGroup = (state) => state.manual.generateZones?.selectedLayerGroup;
export const getSelectedLayers = (state) => state.manual.generateZones?.selectedLayers;
export const isPreviousScript = (state) => {
  return getSelectedManagementZonesType(state) === managementZonesTypes.PREVIOUS_PRESCRIPTION;
};
const getManualLayers = (state) => get(state, 'manual.layers');
export const isGenerateZonesScript = createSelector(
  getSelectedManagementZonesType,
  (managementZonesType) => managementZonesType === managementZonesTypes.GENERATE_ZONES
);
export const isPlantingPlanStep = (state) => {
  const selectedManagementZonesType = getSelectedManagementZonesType(state);
  switch(selectedManagementZonesType) {
    case managementZonesTypes.PREVIOUS_PRESCRIPTION:
    case managementZonesTypes.IMPORTED_MAPS_PARENT:
      return getStep(state) === 3;
    case managementZonesTypes.GENERATE_ZONES:
      return getStep(state) === 4;
    default:
      return getStep(state) === 2;
  }
};
export const isFinalStep = (state) => isPlantingPlanStep(state);

export const isSelectGenerateZonesSelectLayerStep = (state) => {
  return getSelectedManagementZonesType(state) === managementZonesTypes.GENERATE_ZONES && getStep(state) === 2;
};
export const isReviewGenerateZonesStep = (state) => {
  return getSelectedManagementZonesType(state) === managementZonesTypes.GENERATE_ZONES && getStep(state) === 3;
};
export const isSelectPreviousScriptStep = (state) => {
  return isPreviousScript(state) && getStep(state) === 2;
};
export const isSelectImportedScriptStep = (state) => {
  return getSelectedManagementZonesType(state) === managementZonesTypes.IMPORTED_MAPS_PARENT && getStep(state) === 2;
};
export const getAllManagementZones = (state) => get(state, 'manual.allManagementZones');
export const getActiveManagementZones = (state) => get(state, 'manual.managementZones');

export const getAllManagementZonesFlattened = (state) => {
  const { fertilityManagementZones,
    plantingManagementZones,
    importedManagementZones,
    applicationManagementZones } = getAllManagementZones(state);
  if(getSelectedManagementZonesType(state) === managementZonesTypes.IMPORTED_MAPS_PARENT) {
    return orderBy([].concat(importedManagementZones), ['importedDate'],['desc', 'desc']);
  }
  return [].concat(fertilityManagementZones, plantingManagementZones, applicationManagementZones);
};

export const getSortedPreviousPrescriptions = createSelector(
  [getAllManagementZonesFlattened],
  (allManagementZonesFlattened) => orderBy(allManagementZonesFlattened, ['primary', 'lastUpdated'],['desc', 'desc'])
  // filter out scripts that have over 100 zones and then limit the ui to display only 100 scripts
  .filter((script) => Object.keys(script.zones).length <= 100)
  .splice(0, 100)
);

export const getScriptIdByManagementZonesId = (state, managementZonesId) => {
  const zonesData = getAllManagementZonesFlattened(state).find(({ id }) => id === managementZonesId );
  return get(zonesData, 'scriptId');
};
export const getActivityUuidByManagementZonesId = (state, managementZonesId) => {
  const zonesData = getAllManagementZonesFlattened(state).find(({ id }) => id === managementZonesId );
  return get(zonesData, 'activityUuid');
};
export const getManualScriptParamsForTemplateCall = (state) => {
  const { activityUuid, managementZonesId, managementZonesType } = getManualScriptParams(state);
  const scriptIdForManagementZones = getScriptIdByManagementZonesId(state, managementZonesId);
  const plantingPlanParams = getPlantingPlanParams(state);
  const fieldId = getActiveFieldId(state);
  const scriptType = getActiveScriptType(state);

  let cropId;
  if (scriptType === scriptTypes.PLANTING) {
    cropId = getActiveCropId(state);
  }
  return {
    activityUuid,
    cropId,
    fieldId,
    managementZonesId,
    managementZonesType,
    plantingPlanParams,
    scriptIdForManagementZones,
    scriptType
  };
};

export const plantingPlanParamsAreValid = (state) => {
  return !get(getPlantingPlanParamsValidationResult(state), 'isInvalid', false);
};

export const managementZonesIsValid = (state) => {
  const zonesId = getSelectedManagementZonesId(state);
  const allZones = getAllManagementZonesFlattened(state);
  return !!allZones.find((a) => a.id === zonesId);
};

const canPlantingPlanStepGoForward = (state) => {
  const scriptType = getActiveScriptType(state);
  if (scriptType === scriptTypes.APPLICATION && isPlantingPlanStep(state)) {
    const plantingPlanParams = getPlantingPlanParams(state);
    const products = plantingPlanParams.products.APPLICATION_v2 || [];
    return !!products.length;
  }

  return plantingPlanParamsAreValid(state);
};

export const canGoForward = (state) => {
  if (isManagementZonesStep(state)) {
    return !!getSelectedManagementZonesId(state);
  }

  if (isSelectPreviousScriptStep(state)) {
    return managementZonesIsValid(state);
  }

  if(isSelectImportedScriptStep(state)) {
    return managementZonesIsValid(state);
  }

  if (isSelectGenerateZonesSelectLayerStep(state)) {
    const mapLayer = getSelectedMapLayer(state);
    const isMultiLayerEnabled = isMultiLayerZoneGenerationEnabled(state);

    if (isMultiLayerEnabled) {
      const selectedLayers = getSelectedLayers(state) || [];
      return selectedLayers.length > 0;
    }
    // TODO: this may have to be adjusted for when map layers that do not have an image map will be selected
    return !!mapLayer && !!mapLayer.selectedMapType && !!mapLayer.selectedMapUuid;
  }

  if (isPlantingPlanStep(state)) {
    return (canPlantingPlanStepGoForward(state));
  }

  if (isReviewGenerateZonesStep(state)) {
    return !!getGeneratedZones(state);
  }

  return false;
};

export const getManualScriptParamsForCreatedScript = (state) => get(state, 'manual.manualScriptParamsForCreatedScript');

export const getScriptBasedManagementZones = (state) => {
  return {
    fertilityManagementZones: getFertilityManagementZones(state),
    plantingManagementZones: getPlantingManagementZones(state),
    applicationManagementZones: getApplicationManagementZones(state)
  };
};

export const isMultiLayerZoneGenerationEnabled = (state) => state?.launchDarkly?.scriptCreatorMultiLayerZoneGeneration;

const getBaseZoneTypeTiles = (state) => {
  const isMultiLayerEnabled = isMultiLayerZoneGenerationEnabled(state);
  return [{
    description: getMessage(
      isMultiLayerEnabled
        ? 'text__use-generate-zones-multi-layer-description'
        : 'text__use-generate-zones-description'
    ),
    id:  `${managementZonesTypes.GENERATE_ZONES}`,
    managementZonesType: managementZonesTypes.GENERATE_ZONES,
    zones: { 'placeholderIcon': placeHolderIcons.generateZonesPlaceholder }
  }, {
    description: getMessage('text__use-previous-prescription-description'),
    id:  `${managementZonesTypes.PREVIOUS_PRESCRIPTION}`,
    managementZonesType: managementZonesTypes.PREVIOUS_PRESCRIPTION,
    zones: { 'placeholderIcon': placeHolderIcons.usePreviousPrescriptionPlaceholder }
  }, {
    description: getMessage('text__use-imported-maps-description'),
    id:  `${managementZonesTypes.IMPORTED_MAPS_PARENT}`,
    managementZonesType: managementZonesTypes.IMPORTED_MAPS_PARENT,
    zones: { 'placeholderIcon': placeHolderIcons.importedZonesPlaceholder }
  }];
};

export const getBaseManagementZones = (state) => {

  const fieldUuid = getActiveFieldUuid(state);
  const fieldId = getActiveFieldId(state);

  let baseManagementZones = [
    {
      description: getMessage('text__field-boundary-description'),
      id:  `${managementZonesTypes.FIELD_BOUNDARY}`,
      managementZonesType: managementZonesTypes.FIELD_BOUNDARY,
      zones: { 'placeholderIcon': placeHolderIcons.drawZonesPlaceholder }
    }
  ];
  if (isSsurgoEnabled(state.productFeatures, fieldUuid) && fieldHasSsurgoZones(state, fieldId)) {
    baseManagementZones.unshift({
      description: getMessage('text__ssurgo-zones-description'),
      id:  `${managementZonesTypes.SSURGO}`,
      managementZonesType: managementZonesTypes.SSURGO,
      zones: getSsurgoZonesByFieldId(state, fieldId)
    });
  }
  baseManagementZones.unshift(...getBaseZoneTypeTiles(state));
  return baseManagementZones;
};

export const getImportedManagementZones = (state) => {
  const fieldId = getActiveFieldId(state);

  const rawZones = getImportedMapsZonesByFieldId(state, fieldId);
  return rawZones.map(({ meta, zones }) => {
    return {
      id: meta.id,
      importedDate: meta.date,
      managementZonesType: managementZonesTypes.IMPORTED_MAPS,
      name: meta.name,
      polygonal: meta.polygonal,
      zones
    };
  });
};

export const getActiveFieldAvailableLayers = createSelector([
  (state) => getActiveFieldUuid(state),
  (state) => state.manual.layers
],
(activeFieldUuid, layers) => layers?.[activeFieldUuid] || {}
);

export const getLayersTypes = createSelector([
  (state) => getActiveFieldAvailableLayers(state)
],
(layers) => Object.keys(layers).sort((keyA, keyB) => keyA.localeCompare(keyB))
);

export const getYieldLayers = createSelector(
  [
    (state) => getManualLayers(state),
    (state) => getActiveFieldUuid(state)
  ],
  (layers, fieldUuid) => {
    return layers[fieldUuid]?.yield || {};
  }
);

export const getImageDataLayers = (layerGroup) => createSelector(
  [
    (state) => getManualLayers(state),
    (state) => getActiveFieldUuid(state),
  ],
  (layers, fieldUuid) => {
    const cciLayersByMonth = Object.entries(layers[fieldUuid]?.cci?.[layerGroup] || {});
    return cciLayersByMonth.sort((a, b) => {
      return MONTH_ORDER.indexOf(a[0]) - MONTH_ORDER.indexOf(b[0]);
    });
  }
);

export const getYearsFromCciLayers = createSelector(
  [
    (state) => getManualLayers(state),
    (state) => getActiveFieldUuid(state)
  ],
  (layers, fieldUuid) => {
    const cciLayers = layers[fieldUuid]?.cci || {};
    const years = Object.keys(cciLayers);
    return years.sort((a, b) => Number(b) - Number(a));
  }
);

export const getCropsFromYieldLayers = createSelector(
  [
    (state) => getManualLayers(state),
    (state) => getActiveFieldUuid(state),
    (state) => state.crops
  ],
  (layers, fieldUuid, crops) => {
    const yieldLayers = layers[fieldUuid]?.yield || {};
    const cropsArray = [];

    Object.keys(yieldLayers).forEach((cropId) => {
      if (crops[cropId]) {
        cropsArray.push(crops[cropId]);
      }
    });

    return cropsArray.sort((a, b) => a.name.localeCompare(b.name));
  }
);

export const getYieldLayersBySelectedCrop = createSelector(
  [
    (state) => getManualLayers(state),
    (state) => getActiveFieldUuid(state),
    (state) => getSelectedLayerGroup(state)
  ],
  (layers, fieldUuid, selectedLayerGroup) => {
    const selectedYield = layers?.[fieldUuid]?.yield?.[selectedLayerGroup];

    if (!selectedYield) return [];

    return selectedYield.toSorted((a, b) => b.year - a.year);
  }
);

export const getIsLayersRequestPending = (state) => {
  const fieldUuid = getActiveFieldUuid(state);

  return isResourceTypePending({
    state,
    type: `${resourceType.LAYERS_FOR_ZONE_GENERATION}_${fieldUuid}`
  });
};

export const getGenerateZonesReviewSubtitle = createSelector(
  [
    (state) => getSelectedMapLayer(state),
    (state) => getSelectedLayers(state),
    (state) => isMultiLayerZoneGenerationEnabled(state),
    (state) => state.crops,
    (state) => getActiveFieldCountryCode(state)
  ],
  (selectedMapLayer, selectedLayers, isMultiLayerEnabled, crops, countryCode) => {
    const { selectedMapType, selectedMapUuid } = selectedMapLayer;

    if (!isMultiLayerEnabled) {
      return getMessage(
        `title-text__create-manualv2-generate-zones--review-header--${selectedMapType}`,
        { date: formatDate(selectedMapUuid, { locale }) }
      );
    }

    const selectedLayersCount = selectedLayers.length;

    if (!selectedLayersCount) return '';

    if (selectedLayersCount > 1) {
      const multiLayersContent = getMessage(
        'title-text__create-manualv2-generate-multi-layer-zones-review-header-layers',
        { count: selectedLayersCount }
      );
      return getMessage(
        `title-text__create-manualv2-generate-multi-layer-zones-review-header--${selectedMapType}`,
        { content: multiLayersContent }
      );
    }

    const [layer] = selectedLayers;

    const mapTypeFormatters = {
      cci: () => formatDate(layer.eventDate, { locale }),
      yield: () => {
        const { cropId, year, season } = layer;

        const seasonName = LATAM_COUNTRIES.BRAZIL === countryCode
          ? `${getMessage(`season__${SEASONS_ACRONYMS[season]}`)}/` : '';

        return `${
          getMessage(`crop--${crops?.[cropId]?.code}`)
        } - ${seasonName}${
          year
        }`;
      },
    };

    const singleLayerContent = mapTypeFormatters[selectedMapType]?.();

    if (!singleLayerContent) return '';

    return getMessage(
      `title-text__create-manualv2-generate-multi-layer-zones-review-header--${selectedMapType}`,
      { content: singleLayerContent }
    );
  }
);

export const getCropIdsForAvailableDataLayers = (state) => {
  const result = [];

  for (const code of CROP_CODES_FOR_AVAILABLE_LAYERS) {
    const crop = state.crops[code];
    if (crop) {
      result.push(crop['dfp-uuid']);
    }
  }

  return result;
};

export const getLayersError = createSelector([
  (state) => getActiveFieldUuid(state),
  (state) => getManualLayers(state)
],
(activeFieldUuid, layers) => layers?.[activeFieldUuid]?.error
);

export const getCropNominalWeightBySelectedLayerGroup = createSelector([
  (state) => getSelectedLayerGroup(state),
  (state) => state.crops,
],
(selectedLayerGroup, crops) => crops?.[selectedLayerGroup]?.['nominal-weight'],
);
